import { useEffect, useState } from 'react';

/**
 * Il contrasto elevato: verdi, rossi, ambra e blu pieni al posto dei toni pastello.
 *
 * Su un monitor LCD vecchio o slavato il verde tenue del «libero» e il rosa della chiusura
 * diventano lo stesso grigio, e il calendario e la dashboard smettono di dire quel che
 * dicono. Con questa preferenza i fondi si saturano, i bordi si scuriscono e i testi
 * secondari guadagnano nero; il significato dei colori resta lo stesso.
 *
 * Funziona come il buio (`buio.ts`): una preferenza del dispositivo, in `localStorage`,
 * applicata come attributo sul documento prima del primo disegno. Si può anche accendere
 * dall'indirizzo — `?contrasto=alto` — che è comodo per il monitor della dashboard, dove
 * cercare un pulsante non è il modo in cui si configura uno schermo appeso al muro.
 */

const CHIAVE = 'poligono.contrasto';

let acceso = false;
const inAscolto = new Set<() => void>();

function leggi(): boolean {
  try {
    const richiesto = new URLSearchParams(window.location.search).get('contrasto');
    if (richiesto === 'alto' || richiesto === 'normale') {
      window.localStorage.setItem(CHIAVE, richiesto === 'alto' ? 'si' : 'no');
      return richiesto === 'alto';
    }
    return window.localStorage.getItem(CHIAVE) === 'si';
  } catch {
    return false;
  }
}

function applica(): void {
  if (acceso) document.documentElement.setAttribute('data-contrasto', 'alto');
  else document.documentElement.removeAttribute('data-contrasto');
}

export function avviaContrasto(): void {
  acceso = leggi();
  applica();
}

export function alternaContrasto(): void {
  acceso = !acceso;
  try {
    window.localStorage.setItem(CHIAVE, acceso ? 'si' : 'no');
  } catch {
    // Scrittura negata: vale per questa visita.
  }
  applica();
  for (const avvisa of [...inAscolto]) avvisa();
}

export function useContrasto(): boolean {
  const [, ridisegna] = useState(0);
  useEffect(() => {
    const f = () => ridisegna((n) => n + 1);
    inAscolto.add(f);
    return () => {
      inAscolto.delete(f);
    };
  }, []);
  return acceso;
}
